/**
 * PageButton — the standard action button across the app.
 *
 * Replaces the ad-hoc button classes (`.cr-btn-next`, `.rf-btn-submit`,
 * `.rapid-save`, `.vacc-btn--primary`, etc.) with one consistent widget.
 *
 *   <PageButton variant="primary" startIcon={<FiSave />}>Save</PageButton>
 *   <PageButton variant="success">Submit registration</PageButton>
 *   <PageButton variant="ghost" startIcon={<FiArrowLeft />}>Back</PageButton>
 *   <PageButton variant="secondary">Export PDF</PageButton>
 *   <PageButton variant="danger">Delete</PageButton>
 *
 * Variants:
 *   - primary   sky-500 contained, used for "Next" / "Save" / "Add"
 *   - success   emerald-500 contained, used for the final "Submit"
 *   - secondary surface-2 contained, used for "Export PDF" / "Reports"
 *   - danger    rose-500 contained, used for destructive actions
 *   - ghost     transparent outlined, used for "Back" / "Print" / "Cancel"
 *
 * Size defaults to MUI `medium`; pass `size="small"` for compact contexts
 * (e.g. toolbar buttons). All other props are forwarded to MUI Button.
 */
import React from "react";
import Button from "@mui/material/Button";

const VARIANT_TO_MUI = {
    primary: { variant: "contained", color: "primary" },
    success: { variant: "contained", color: "success" },
    secondary: { variant: "contained", color: "secondary" },
    danger: { variant: "contained", color: "error" },
    ghost: { variant: "outlined", color: "primary" },
};

export function PageButton({
    variant = "primary",
    size = "medium",
    fullWidth = false,
    startIcon,
    endIcon,
    children,
    sx,
    ...rest
}) {
    const mui = VARIANT_TO_MUI[variant] || VARIANT_TO_MUI.primary;
    const isGhost = variant === "ghost";
    return (
        <Button
            variant={mui.variant}
            color={mui.color}
            size={size}
            fullWidth={fullWidth}
            startIcon={startIcon}
            endIcon={endIcon}
            sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "var(--pt-radius-md)",
                letterSpacing: 0,
                whiteSpace: "nowrap",
                boxShadow: isGhost
                    ? "none"
                    : "0 6px 16px color-mix(in srgb, currentColor 22%, transparent)",
                "&:hover": {
                    boxShadow: isGhost
                        ? "none"
                        : "0 8px 20px color-mix(in srgb, currentColor 28%, transparent)",
                },
                ...(sx || {}),
            }}
            {...rest}
        >
            {children}
        </Button>
    );
}

export default PageButton;
